<template>
  <div v-if="dhmeBillOfMaterialStatus === 'success'" class="d-flex flex-column">
    <module-navigation-bar title="Bill of Material">
      <template slot="module-nav-actions">
        <v-tabs
          v-model="bomTab"
          background-color="transparent"
          height="46"
          style="width: auto"
        >
          <v-tab :disabled="sortedGroupsByCode.length === 0">
            {{ $t('modules.BillOfMaterial.container.element') }}
          </v-tab>
          <v-tab :disabled="sortedGroupsByCode.length === 0">
            {{ $t('modules.BillOfMaterial.container.object') }}
          </v-tab>
          <v-tab>
            {{ $t('modules.BillOfMaterial.container.group') }}
          </v-tab>
        </v-tabs>
      </template>
      <template slot="module-nav-logo">
        <img
          v-if="isDaiwaLicense"
          alt=""
          height="40px"
          src="https://www.daiwahousemodular.eu/wp-content/uploads/daiwa_logo.svg"
        />
      </template>
    </module-navigation-bar>
    <div class="d-flex flex-1 flex-scroll-height overflow-hidden">
      <div
        class="background-white ant-border-right radius-0 overflow-y-auto"
        style="width: 300px"
      >
        <div class="supplier-list flex-grow-1 px-2">
          <v-list dense>
            <v-subheader class="pa-0">
              {{ $t('modules.BillOfMaterial.container.groupHeader') }}
              <v-spacer />
              <transition name="simple-fade">
                <v-btn v-if="bomTab === 2" icon @click="openCreateGroup">
                  <v-icon>mdi-plus</v-icon>
                </v-btn>
              </transition>
            </v-subheader>
            <v-list-item-group v-model="groupIndex" color="primary">
              <v-list-item
                v-if="bomTab !== 2"
                class="d-flex align-center pa-2 group-list-item"
                @click="
                  focusOnGroup({
                    group_code: '-1',
                    id: '-1',
                    name: $t('modules.BillOfMaterial.container.unlinked'),
                  })
                "
              >
                <div class="flex-grow-1">
                  {{ $t('modules.BillOfMaterial.container.unlinked') }}
                </div>
              </v-list-item>
              <v-list-item
                v-for="(group, index) in sortedGroupsByCode"
                :key="group.id"
                class="d-flex align-center pa-2 group-list-item"
                @click="focusOnGroup(group)"
              >
                <div class="flex-grow-1">
                  {{ group?.group_code + ' | ' + group.name }}
                </div>
                <v-btn
                  v-if="bomTab === 2"
                  class="group-delete-btn"
                  icon
                  small
                  @click.stop="setupGroupDelete(group)"
                >
                  <v-icon small> mdi-trash-can</v-icon>
                </v-btn>
              </v-list-item>
            </v-list-item-group>
          </v-list>

          <!-- Add group dialog -->
          <v-dialog
            key="add-group"
            v-model="groupDialog"
            max-width="500px"
            @click:outside="closeGroupDialogs()"
          >
            <v-card>
              <v-card-title>
                {{ $t('modules.BillOfMaterial.container.addGroup') }}
              </v-card-title>

              <!-- Validation form -->
              <v-form
                ref="groupForm"
                v-model="groupFormValid"
                lazy-validation
                @submit.prevent="submitCreateGroup"
              >
                <v-card-text>
                  <v-container>
                    <v-row>
                      <v-col cols="12" md="12" sm="6">
                        <ant-input label="Code" top-margin="mt-0">
                          <template #input-field>
                            <v-text-field
                              v-model="groupItem.group_code"
                              :rules="[rules.required]"
                              autofocus
                              dense
                              filled
                              label="Code"
                              single-line
                              @keydown.enter.prevent="submitCreateGroup"
                            />
                          </template>
                        </ant-input>

                        <ant-input label="Name" top-margin="mt-0">
                          <template #input-field>
                            <v-text-field
                              v-model="groupItem.name"
                              :rules="[rules.required]"
                              dense
                              filled
                              label="Name"
                              single-line
                              @keydown.enter.prevent="submitCreateGroup"
                            />
                          </template>
                        </ant-input>
                      </v-col>
                    </v-row>
                  </v-container>
                </v-card-text>

                <v-card-actions>
                  <v-spacer />
                  <v-btn color="red" text @click="closeGroupDialogs()">
                    Cancel
                  </v-btn>
                  <v-btn
                    color="primary"
                    elevation="0"
                    :disabled="!groupFormValid || !canSaveGroup"
                    type="submit"
                  >
                    Save
                  </v-btn>
                </v-card-actions>
              </v-form>
            </v-card>
          </v-dialog>

          <!-- Delete group dialog -->
          <delete-dialog
            :dialog="groupDeleteDialog"
            :title="`Are you sure you want to delete group: ${groupDeleteItem.name}?`"
            @closeDialog="closeGroupDialogs"
            @deleteAction="removeGroup"
          />
        </div>
      </div>

      <div
        v-if="dhmeBillOfMaterialFocusedGroup"
        class="d-flex flex-1 flex-column"
      >
        <div class="d-flex mb-5 pa-5">
          <div class="d-flex flex-column">
            <div class="font-weight-bold" style="font-size: 18px">
              {{
                $t('modules.BillOfMaterial.elementSchedule.qs', {
                  name: dhmeBillOfMaterialFocusedGroup.name,
                })
              }}
            </div>

            <v-menu
              v-model="nameMenu"
              :close-on-content-click="false"
              :nudge-width="200"
              offset-x
            >
              <template #activator="{ on, attrs }">
                <span class="font-italic" v-bind="attrs" v-on="on">
                  {{ dhmeBillOfMaterialFocusedGroup.group_code }} |
                  {{ dhmeBillOfMaterialFocusedGroup.name }}

                  <v-icon class="ml-2" small>mdi-pencil</v-icon>
                </span>
              </template>
              <v-card>
                <v-text-field
                  v-model="updatedName"
                  autofocus
                  dense
                  filled
                  hide-details
                  single-line
                  @keydown.enter="updateName"
                />
              </v-card>
            </v-menu>
          </div>
          <v-spacer />
          <transition name="simple-fade">
            <v-btn-toggle
              v-if="bomTab === 0 || bomTab === 1"
              v-model="tableTabState"
              background-color="transparent"
              class="d-flex align-center"
              color="primary"
              mandatory
              rounded
            >
              <v-btn class="ma-0" value="production">
                {{ $t('modules.BillOfMaterial.elementSchedule.production') }}
              </v-btn>

              <v-btn class="ma-0" value="placement">
                {{ $t('modules.BillOfMaterial.elementSchedule.placement') }}
              </v-btn>
            </v-btn-toggle>
          </transition>
        </div>

        <div
          class="flex-1 flex-scroll-height overflow-y-auto ant-glass-background ant-border-top radius-0"
        >
          <transition-group mode="out-in" name="slide-fade-down" tag="div">
            <b-o-m-element-schedule
              v-if="bomTab === 0"
              key="bom-elements"
              :table-tab-state="tableTabState"
              class="pos-abs"
            />
            <b-o-m-object-schedule
              v-else-if="bomTab === 1"
              key="bom-objects"
              :table-tab-state="tableTabState"
              class="pos-abs"
            />
            <b-o-m-group-management
              v-if="bomTab === 2"
              key="bom-groups"
              class="pos-abs"
            />
          </transition-group>
        </div>
      </div>
    </div>
  </div>
  <div v-else class="d-flex justify-center align-center">
    <ant-loading />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import ModuleNavigationBar from '@/components/Modules/ModuleNavigationBar.vue';
import DeleteDialog from '@/components/DeleteDialog.vue';
import AntLoading from '@/components/AntLoading.vue';
import BOMElementSchedule from '@/modules/daiwa-house-modular-europe/BillOfMaterial/DhmeBillOfMaterialElements.vue';
import BOMObjectSchedule from '@/modules/daiwa-house-modular-europe/BillOfMaterial/DhmeBillOfMaterialObjects.vue';
import BOMGroupManagement from '@/modules/daiwa-house-modular-europe/BillOfMaterial/DhmeBillOfMaterialGroups.vue';
import AntInput from '@/components/AntInput.vue';

export default {
  name: 'DhmeBillOfMaterial',
  components: {
    AntInput,
    BOMGroupManagement,
    BOMObjectSchedule,
    BOMElementSchedule,
    AntLoading,
    DeleteDialog,
    ModuleNavigationBar,
  },
  data: () => {
    return {
      nameMenu: false,
      updatedName: '',
      bomTab: 0,
      groupDialog: false,
      groupItem: { group_code: '', name: '' },
      groupDeleteDialog: false,
      groupDeleteItem: {},
      sidebarSize: 300,
      tableTabState: 'production',
      groupIndex: null,

      // dialog form state
      groupFormValid: false,
      rules: {
        required: (v) => !!(v && `${v}`.trim()) || 'Required',
      },
    };
  },
  computed: {
    ...mapGetters([
      'project',
      'dhmeBillOfMaterialFocusedGroup',
      'dhmeBillOfMaterialStatus',
      'dhmeBillOfMaterialResourceGroups',
      'isDaiwaLicense',
    ]),

    sortedGroupsByCode() {
      return [...this.dhmeBillOfMaterialResourceGroups].sort(function (a, b) {
        return a.group_code - b.group_code;
      });
    },

    // button guard to prevent empty/whitespace-only values
    canSaveGroup() {
      const code = this.groupItem?.group_code;
      const name = this.groupItem?.name;
      return !!(code && `${code}`.trim() && name && `${name}`.trim());
    },
  },
  watch: {
    dhmeBillOfMaterialFocusedGroup(value) {
      this.updatedName = value?.name;
    },
  },
  async mounted() {
    await this.$store.dispatch('fetchBillOfMaterialData');
    await this.$store.dispatch('fetchBillOfMaterialTasks');
    if (this.sortedGroupsByCode.length > 0) {
      await this.$store.dispatch('focusOnGroup', this.sortedGroupsByCode[0]);
      this.groupIndex = 1;
    } else {
      this.bomTab = 2;
    }
  },
  methods: {
    openCreateGroup() {
      this.groupItem = { group_code: '', name: '' };
      this.groupDialog = true;
      this.$nextTick(() => {
        if (this.$refs.groupForm?.resetValidation) {
          this.$refs.groupForm.resetValidation();
        }
      });
    },

    focusOnGroup(group) {
      this.$store.dispatch('focusOnGroup', group);
    },

    async updateName() {
      if (this.updatedName.length === 0) {
        this.$store.commit('showNotification', {
          content: 'Please enter a valid name, name cannot be empty',
          color: 'error',
        });
        return;
      }
      await this.$store.dispatch('updateResourceGroupName', {
        name: this.updatedName,
      });
      this.nameMenu = false;
    },

    submitCreateGroup() {
      // run Vuetify validation and also guard against whitespace-only values
      const ok = this.$refs.groupForm?.validate
        ? this.$refs.groupForm.validate()
        : this.canSaveGroup;

      if (!ok || !this.canSaveGroup) return;

      if (
        this.dhmeBillOfMaterialResourceGroups
          .map((g) => g.group_code)
          .includes(this.groupItem.group_code)
      ) {
        this.$store.commit('showNotification', {
          content: 'Group code already exists',
          color: 'error',
        });
        return;
      }

      this.createGroup();
    },

    createGroup() {
      this.$store
        .dispatch('createResourceGroup', {
          record: {
            group_code: `${this.groupItem.group_code}`.trim(),
            name: `${this.groupItem.name}`.trim(),
          },
        })
        .then(() => {
          this.closeGroupDialogs();
        });
    },

    closeGroupDialogs() {
      this.groupItem = { group_code: '', name: '' };
      this.groupDeleteItem = {};
      this.groupDialog = false;
      this.groupDeleteDialog = false;
      if (this.$refs.groupForm?.resetValidation) {
        this.$refs.groupForm.resetValidation();
      }
    },

    setupGroupDelete(group) {
      this.groupDeleteItem = Object.assign({}, group);
      this.groupDeleteDialog = true;
    },

    async removeGroup() {
      await this.$store.dispatch('removeResourceGroup', this.groupDeleteItem);
      this.closeGroupDialogs();
    },
  },
};
</script>

<style lang="scss" scoped>
.group-list-item {
  transition: 200ms ease-out;

  .group-delete-btn {
    opacity: 0;
    transition: 200ms;
  }

  &:hover {
    .group-delete-btn {
      opacity: 1;
    }
  }
}
</style>
